import { FOUR_WEEK_MINIMUM_PRICE_THRESHOLD } from '@app/config/price';
import { Price } from '@app/types/equipment';

/**
 * Safely parses a price value to a number, handling various input types
 * @param price - The price value to parse (can be string, number, null, undefined)
 * @returns Parsed number or null if invalid
 */
export const parsePrice = (price: string | number | null | undefined): number | null => {
  if (price === null || price === undefined) {
    return null;
  }

  let parsedValue: number;
  
  if (typeof price === 'string') {
    // Handle empty strings
    if (price.trim() === '') {
      return null;
    }
    parsedValue = parseFloat(price);
  } else if (typeof price === 'number') {
    parsedValue = price;
  } else {
    return null;
  }

  // Check if the parsed value is a valid number
  if (isNaN(parsedValue) || !isFinite(parsedValue)) {
    return null;
  }

  return parsedValue;
};

/**
 * Checks if a price is valid (not null, undefined, or NaN)
 * @param price - The price value to check
 * @returns true if price is valid
 */
export const isValidPrice = (price: string | number | null | undefined): boolean => {
  const parsedPrice = parsePrice(price);
  return parsedPrice !== null;
};

/**
 * A 4-week/monthly price is considered valid if it greater than or equal to the threshold (1)
 * @param price - The price value to check
 * @returns true if price equals the threshold
 */
export const isValidFourWeekPrice = (price: string | number | null | undefined): boolean => {
  const parsedPrice: number | null = parsePrice(price);
  return parsedPrice !== null && parsedPrice >= FOUR_WEEK_MINIMUM_PRICE_THRESHOLD;
};

/**
 * Checks if contact for price message should be shown for a given price
 * - If price is null/undefined, returns true (show message)
 * - If price per month is not a valid number or less than the threshold, returns true (show message)
 * @param price - The price value to check
 * @returns true if should show contact message (valid price that's not the threshold)
 */
export const showContactForPrice = (price: Price | undefined | null): boolean => {
  return !price || !isValidFourWeekPrice(price.price_per_month);
};